import React, { ReactNode } from 'react';
import { AlertTriangle, CheckCircle, Info, AlertCircle, LucideIcon } from 'lucide-react';
import { Button } from '../button/Button';
import { LoadingButton } from '../button/LoadingButton';
import { BaseModal, BaseModalProps } from './BaseModal';

export interface ConfirmationModalProps extends Omit<BaseModalProps, 'children'> {
  variant?: 'danger' | 'warning' | 'info' | 'success';
  description?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  icon?: LucideIcon;
  showIcon?: boolean;
  children?: ReactNode;
}

// Variant configurations for confirmation modals. The confirm button uses a
// Button variant where a theme token exists (danger → the destructive token,
// matching the Delete button that opened the modal; info → the brand primary);
// warning and success keep literal amber/green, which are semantic colours.
const variantConfig: Record<
  NonNullable<ConfirmationModalProps['variant']>,
  {
    icon: LucideIcon;
    iconColor: string;
    iconBg: string;
    confirmVariant: 'default' | 'destructive';
    confirmButtonClass: string;
  }
> = {
  danger: {
    icon: AlertTriangle,
    iconColor: 'text-red-600 dark:text-red-400',
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    confirmVariant: 'destructive',
    confirmButtonClass: ''
  },
  warning: {
    icon: AlertCircle,
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    confirmVariant: 'default',
    confirmButtonClass: 'bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:text-gray-950 dark:hover:bg-amber-400 text-white'
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    confirmVariant: 'default',
    confirmButtonClass: ''
  },
  success: {
    icon: CheckCircle,
    iconColor: 'text-green-600 dark:text-green-400',
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    confirmVariant: 'default',
    confirmButtonClass: 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:text-gray-950 dark:hover:bg-green-400 text-white'
  }
};

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  variant = 'info',
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  confirmDisabled = false,
  icon,
  title,
  onClose,
  showIcon = true,
  children,
  ...baseProps
}) => {
  const config = variantConfig[variant];
  const IconComponent = icon || config.icon;

  // Render title with optional icon
  const titleWithIcon = showIcon && IconComponent ? (
    <div className="flex items-center gap-3">
      <div data-testid="confirmation-icon" className={`w-8 h-8 ${config.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
        <IconComponent className={`h-4 w-4 ${config.iconColor}`} />
      </div>
      <span>{title}</span>
    </div>
  ) : title;

  // Render footer buttons
  const footerContent = (
    <div className="flex items-center justify-end gap-3">
      <Button
        variant="outline"
        onClick={onCancel || onClose}
        disabled={isLoading}
      >
        {cancelText}
      </Button>
      <LoadingButton
        onClick={onConfirm}
        loading={isLoading}
        disabled={confirmDisabled}
        variant={config.confirmVariant}
        className={config.confirmButtonClass}
      >
        {confirmText}
      </LoadingButton>
    </div>
  );

  return (
    <BaseModal
      {...baseProps}
      title={titleWithIcon}
      onClose={onClose}
      contentClassName="px-6 py-4"
      footer={footerContent}
    >
      {/* Content */}
      {children || (description && (
        <div data-testid="confirmation-description" className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
          {typeof description === 'string' ? <p>{description}</p> : description}
        </div>
      ))}
    </BaseModal>
  );
};
